<template>
  <div class="user-clause-library">
    <AppSectionHero
      :title="$t('pages.user.clauseLibrary.title')"
      :subtitle="$t('pages.user.clauseLibrary.subtitle')"
      :stats="heroStats"
      :show-refresh="false"
      @stat-click="selectLibraryFilter"
    />

    <CCard class="border-0 shadow-sm create-shell">
      <CCardBody class="p-4 create-shell__body">
        <div>
          <div class="create-shell__eyebrow">{{ text.startHere }}</div>
          <div class="create-shell__title">{{ text.createTitle }}</div>
          <div class="create-shell__copy">{{ text.createCopy }}</div>
        </div>
        <button v-if="canManageClauses" class="primary-btn create-shell__button" @click="openCreate">
          <CIcon name="cil-plus" />
          {{ text.addMyClause }}
        </button>
        <div v-else class="create-shell__note">{{ text.viewOnlyNote }}</div>
      </CCardBody>
    </CCard>

    <CCard class="border-0 shadow-sm filter-shell">
      <CCardBody class="p-4">
        <div class="toolbar">
          <div class="search-box">
            <span class="search-icon">🔍</span>
            <input v-model="searchQuery" :placeholder="text.searchPlaceholder">
          </div>
          <select v-model="selectedCategory" class="filter-select">
            <option v-for="item in categoryOptions" :key="item" :value="item">{{ item }}</option>
          </select>
        </div>
      </CCardBody>
    </CCard>

    <CCard class="border-0 shadow-sm card-shell">
      <div class="card-header">
        <div>
          <div class="result-title">{{ text.directoryTitle }}</div>
          <div class="result-copy">{{ text.showing(filteredClauses.length) }}</div>
        </div>
        <span class="result-badge">{{ selectedCategoryLabel }}</span>
      </div>

      <CCardBody class="p-0">
        <div v-if="filteredClauses.length" class="clause-list">
          <div 
            v-for="item in filteredClauses" 
            :key="item.id" 
            class="clause-card"
            :class="{ 'clause-card--expanded': expandedClauseIds.includes(item.id) }"
          >
            <div class="clause-card__header">
              <div class="clause-card__eyebrow">
                <span class="category-pill">{{ item.category }}</span>
                <span class="source-pill">{{ item.sourceType === 'default' ? text.starter : text.myClause }}</span>
              </div>
              <div class="clause-title">{{ item.title }}</div>
            </div>
            <div 
              class="clause-content"
              :class="{ 'clause-content--collapsed': !expandedClauseIds.includes(item.id) }"
              @click="toggleClauseExpand(item.id)"
            >
              {{ item.content }}
              <button 
                v-if="shouldShowExpandButton(item.content)"
                type="button"
                class="clause-content__toggle"
                :class="{ 'clause-content__toggle--expanded': expandedClauseIds.includes(item.id) }"
                @click.stop="toggleClauseExpand(item.id)"
              >
                {{ expandedClauseIds.includes(item.id) ? text.showLess : text.showMore }}
              </button>
            </div>
            <div class="clause-card__footer">
              <button class="ghost-btn" @click="viewClause(item)">{{ text.details }}</button>
              <template v-if="canManageClauses && item.editable">
                <button class="save-btn save-btn--soft" @click="openEdit(item)">{{ text.edit }}</button>
                <button class="danger-btn" @click="removeClause(item)">{{ text.delete }}</button>
              </template>
            </div>
          </div>
        </div>

        <div v-else class="empty-state">
          <div class="empty-state__icon">📚</div>
          <div class="empty-state__title">{{ text.emptyTitle }}</div>
          <div class="empty-state__copy">{{ text.emptyCopy }}</div>
        </div>
      </CCardBody>
    </CCard>

    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-card">
        <div class="modal-header">
          <h3>{{ editingId ? text.editClause : text.addClause }}</h3>
          <button class="close-btn" @click="closeModal">x</button>
        </div>
        <div class="modal-body">
          <label class="field"><span>{{ text.fields.title }}</span><input v-model="form.title"></label>
          <label class="field"><span>{{ text.fields.category }}</span><input v-model="form.category"></label>
          <label class="field"><span>{{ text.fields.appliesTo }}</span><input v-model="form.appliesToText" :placeholder="text.appliesToPlaceholder"></label>
          <label class="field"><span>{{ text.fields.content }}</span><textarea v-model="form.content" rows="6"></textarea></label>
        </div>
        <div class="modal-actions">
          <button class="ghost-btn" @click="closeModal">{{ text.cancel }}</button>
          <button class="save-btn" @click="saveClause">{{ text.save }}</button>
        </div>
      </div>
    </div>

    <div v-if="viewingClause" class="modal-overlay" @click.self="viewingClause = null">
      <div class="modal-card">
        <div class="modal-header">
          <h3>{{ viewingClause.title }}</h3>
          <button class="close-btn" @click="viewingClause = null">x</button>
        </div>
        <div class="modal-body">
          <div class="detail-row">
            <span>{{ text.fields.category }}</span>
            <strong>{{ viewingClause.category }}</strong>
          </div>
          <div class="detail-row">
            <span>{{ text.fields.source }}</span>
            <strong>{{ viewingClause.sourceType === 'default' ? text.starter : text.myClause }}</strong>
          </div>
          <div class="detail-row detail-row--stacked">
            <span>{{ text.fields.clauseContent }}</span>
            <div class="detail-copy">{{ viewingClause.content }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Service from '@/service/api'
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import { localizeClauseRecord } from '@/utils/clause-localization'
import { showErrorAlert } from '@/utils/sweet-alert'

const PAGE_TEXT = {
  en: {
    startHere: 'Start Here',
    createTitle: 'Clause access for your workspace',
    createCopy: 'Browse starter clauses and clause records here. Creating, editing, and deleting clauses is limited to admin and committee members.',
    addMyClause: 'Add My Clause',
    viewOnlyNote: 'This library is view-only for user accounts. Clause add, edit, and delete actions are limited to admin and committee.',
    searchPlaceholder: 'Search clauses by title, text, or category',
    directoryTitle: 'Clause Directory',
    showing: (count) => `Showing ${count} clauses`,
    starter: 'Starter',
    myClause: 'My Clause',
    details: 'Details',
    edit: 'Edit',
    delete: 'Delete',
    showMore: 'Show More',
    showLess: 'Show Less',
    emptyTitle: 'No clauses found',
    emptyCopy: 'Try adjusting the library card, category filter, or search.',
    editClause: 'Edit Clause',
    addClause: 'Add Clause',
    fields: {
      title: 'Title',
      category: 'Category',
      appliesTo: 'Applies To',
      content: 'Content',
      source: 'Source',
      clauseContent: 'Clause Content'
    },
    appliesToPlaceholder: 'Exchange Student, MOU File',
    cancel: 'Cancel',
    save: 'Save',
    allCategories: 'All Categories',
    general: 'General',
    mouFile: 'MOU File',
    stats: {
      allClauses: 'All Clauses',
      starter: 'Starter',
      myClauses: 'My Clauses'
    },
    accessRestricted: 'Access Restricted',
    addRestricted: 'Only admin and committee members can add clauses.',
    editRestricted: 'Only admin and committee members can edit clauses.',
    saveRestricted: 'Only admin and committee members can save clauses.',
    deleteRestricted: 'Only admin and committee members can delete clauses.'
  },
  th: {
    startHere: 'เริ่มที่นี่',
    createTitle: 'การเข้าถึงข้อกำหนดในพื้นที่ทำงานของคุณ',
    createCopy: 'เรียกดูข้อกำหนดเริ่มต้นและรายการข้อกำหนดได้ที่นี่ การเพิ่ม แก้ไข และลบข้อกำหนดจำกัดไว้สำหรับผู้ดูแลระบบและคณะกรรมการ',
    addMyClause: 'เพิ่มข้อกำหนดของฉัน',
    viewOnlyNote: 'คลังนี้เป็นแบบอ่านอย่างเดียวสำหรับบัญชีผู้ใช้ การเพิ่ม แก้ไข และลบข้อกำหนดจำกัดไว้สำหรับผู้ดูแลระบบและคณะกรรมการ',
    searchPlaceholder: 'ค้นหาข้อกำหนดตามชื่อ เนื้อหา หรือหมวดหมู่',
    directoryTitle: 'สารบบข้อกำหนด',
    showing: (count) => `แสดงข้อกำหนด ${count} รายการ`,
    starter: 'เริ่มต้น',
    myClause: 'ข้อกำหนดของฉัน',
    details: 'รายละเอียด',
    edit: 'แก้ไข',
    delete: 'ลบ',
    showMore: 'แสดงเพิ่มเติม',
    showLess: 'แสดงน้อยลง',
    emptyTitle: 'ไม่พบข้อกำหนด',
    emptyCopy: 'ลองปรับตัวกรองหมวดหมู่หรือคำค้นหา',
    editClause: 'แก้ไขข้อกำหนด',
    addClause: 'เพิ่มข้อกำหนด',
    fields: {
      title: 'ชื่อ',
      category: 'หมวดหมู่',
      appliesTo: 'ใช้กับ',
      content: 'เนื้อหา',
      source: 'แหล่งที่มา',
      clauseContent: 'เนื้อหาข้อกำหนด'
    },
    appliesToPlaceholder: 'Exchange Student, MOU File',
    cancel: 'ยกเลิก',
    save: 'บันทึก',
    allCategories: 'ทุกหมวดหมู่',
    general: 'ทั่วไป',
    mouFile: 'MOU File',
    stats: {
      allClauses: 'ข้อกำหนดทั้งหมด',
      starter: 'เริ่มต้น',
      myClauses: 'ข้อกำหนดของฉัน'
    },
    accessRestricted: 'จำกัดการเข้าถึง',
    addRestricted: 'เฉพาะผู้ดูแลระบบและคณะกรรมการเท่านั้นที่สามารถเพิ่มข้อกำหนดได้',
    editRestricted: 'เฉพาะผู้ดูแลระบบและคณะกรรมการเท่านั้นที่สามารถแก้ไขข้อกำหนดได้',
    saveRestricted: 'เฉพาะผู้ดูแลระบบและคณะกรรมการเท่านั้นที่สามารถบันทึกข้อกำหนดได้',
    deleteRestricted: 'เฉพาะผู้ดูแลระบบและคณะกรรมการเท่านั้นที่สามารถลบข้อกำหนดได้'
  }
}

function collectRoleTokens(profile) {
  const tokens = new Set()
  const pushRole = (role) => {
    if (!role) return
    if (typeof role === 'string') {
      const normalized = String(role).trim().toLowerCase()
      if (normalized) tokens.add(normalized)
      return
    }
    if (Array.isArray(role)) {
      role.forEach(pushRole)
      return
    }
    if (typeof role === 'object') {
      ;[
        role.name,
        role.title,
        role.value,
        role.code,
        role.key,
        role.slug,
        role.role,
        role.roleName,
        role.label,
        role.groupLabel
      ].forEach(item => {
        if (item) pushRole(item)
      })
    }
  }

  if (!profile || typeof profile !== 'object') return tokens
  pushRole(profile.role)
  pushRole(profile.roleName)
  pushRole(profile.type)
  if (Array.isArray(profile.roles)) profile.roles.forEach(pushRole)
  if (Array.isArray(profile.securityGroups)) profile.securityGroups.forEach(pushRole)
  return tokens
}

export default {
  name: 'UserClauseLibrary',
  components: { AppSectionHero },
  data() {
    return {
      clauses: [],
      searchQuery: '',
      activeLibraryFilter: 'all',
      selectedCategory: PAGE_TEXT.th.allCategories,
      showModal: false,
      viewingClause: null,
      editingId: '',
      form: { title: '', category: PAGE_TEXT.th.general, content: '', appliesToText: PAGE_TEXT.th.mouFile },
      expandedClauseIds: []
    }
  },
  computed: {
    localeCode() {
      return String(this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'en').toLowerCase().startsWith('th') ? 'th' : 'en'
    },
    text() {
      return PAGE_TEXT[this.localeCode]
    },
    localizedClauses() {
      return this.clauses.map(item => localizeClauseRecord(item, this.localeCode))
    },
    canManageClauses() {
      const roles = collectRoleTokens(this.$store.getters['auth/profile'] || null)
      return roles.has('admin') || roles.has('administrator') || roles.has('committee') || roles.has('committe')
    },
    heroStats() {
      return [
        { key: 'all', label: this.text.stats.allClauses, value: this.localizedClauses.length, icon: 'cil-book', iconClass: 'app-section-stat__icon--total', clickable: true, active: this.activeLibraryFilter === 'all' },
        { key: 'starter', label: this.text.stats.starter, value: this.localizedClauses.filter(item => item.sourceType === 'default').length, icon: 'cil-library', iconClass: 'app-section-stat__icon--active', clickable: true, active: this.activeLibraryFilter === 'starter' },
        { key: 'my-clauses', label: this.text.stats.myClauses, value: this.localizedClauses.filter(item => item.sourceType !== 'default').length, icon: 'cil-user', iconClass: 'app-section-stat__icon--attention', clickable: true, active: this.activeLibraryFilter === 'my-clauses' }
      ]
    },
    categoryOptions() {
      return [this.text.allCategories].concat([...new Set(this.localizedClauses.map(item => String(item.category || this.text.general)).filter(Boolean))].sort())
    },
    selectedCategoryLabel() {
      return this.selectedCategory
    },
    filteredClauses() {
      const libraryFiltered = this.localizedClauses.filter(item => this.matchesLibraryFilter(item))
      const categoryFiltered = this.selectedCategory === this.text.allCategories
        ? libraryFiltered
        : libraryFiltered.filter(item => String(item.category || '').toLowerCase() === this.selectedCategory.toLowerCase())
      if (!this.searchQuery) return categoryFiltered
      const query = this.searchQuery.toLowerCase()
      return categoryFiltered.filter(item =>
        String(item.title).toLowerCase().includes(query) ||
        String(item.content).toLowerCase().includes(query) ||
        String(item.category).toLowerCase().includes(query)
      )
    }
  },
  mounted() {
    this.selectedCategory = this.text.allCategories
    this.loadClauses()
  },
  watch: {
    localeCode() {
      this.selectedCategory = this.text.allCategories
      if (this.viewingClause && this.viewingClause.id) {
        const rawItem = this.clauses.find(entry => String(entry.id || entry._id || '') === String(this.viewingClause.id))
        if (rawItem) this.viewingClause = localizeClauseRecord(rawItem, this.localeCode)
      }
      if (!this.editingId && !this.showModal) {
        this.form = { title: '', category: this.text.general, content: '', appliesToText: this.text.mouFile }
      }
    }
  },
  methods: {
    matchesLibraryFilter(item) {
      if (this.activeLibraryFilter === 'all') return true
      if (this.activeLibraryFilter === 'starter') return item.sourceType === 'default'
      if (this.activeLibraryFilter === 'my-clauses') return item.sourceType !== 'default'
      return true
    },
    selectLibraryFilter(stat) {
      const nextFilter = String(stat && stat.key ? stat.key : 'all')
      this.activeLibraryFilter = this.activeLibraryFilter === nextFilter ? 'all' : nextFilter
    },
    async loadClauses() {
      const response = await Service.agreements('user-clauses')
      const rows = response && response.data && response.data.data ? response.data.data : []
      this.clauses = rows.map(item => ({
        ...item,
        id: item && (item.id || item._id) ? String(item.id || item._id) : ''
      }))
    },
    openCreate() {
      if (!this.canManageClauses) {
        showErrorAlert(this.text.addRestricted, this.text.accessRestricted)
        return
      }
      this.editingId = ''
      this.form = { title: '', category: this.text.general, content: '', appliesToText: this.text.mouFile }
      this.showModal = true
    },
    openEdit(item) {
      if (!this.canManageClauses) {
        showErrorAlert(this.text.editRestricted, this.text.accessRestricted)
        return
      }
      const rawItem = this.clauses.find(entry => String(entry.id || entry._id || '') === String(item.id || item._id || '')) || item
      this.editingId = rawItem.id || rawItem._id || ''
      this.form = {
        title: rawItem.title,
        category: rawItem.category,
        content: rawItem.content,
        appliesToText: (rawItem.appliesTo || []).join(', ')
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
    },
    viewClause(item) {
      this.viewingClause = item
    },
    async saveClause() {
      if (!this.canManageClauses) {
        showErrorAlert(this.text.saveRestricted, this.text.accessRestricted)
        return
      }
      const payload = {
        title: this.form.title,
        category: this.form.category,
        content: this.form.content,
        appliesTo: this.form.appliesToText.split(',').map(item => item.trim()).filter(Boolean)
      }
      if (this.editingId) await Service.agreements('update-clause', { id: this.editingId, ...payload })
      else await Service.agreements('create-clause', payload)
      this.closeModal()
      this.loadClauses()
    },
    async removeClause(item) {
      if (!this.canManageClauses) {
        showErrorAlert(this.text.deleteRestricted, this.text.accessRestricted)
        return
      }
      await Service.agreements('delete-clause', { id: item.id || item._id })
      this.loadClauses()
    },
    shouldShowExpandButton(content) {
      return String(content || '').length > 200
    },
    toggleClauseExpand(clauseId) {
      const id = String(clauseId || '').trim()
      if (!id) return
      
      const index = this.expandedClauseIds.indexOf(id)
      if (index > -1) {
        this.expandedClauseIds.splice(index, 1)
      } else {
        this.expandedClauseIds.push(id)
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("user-clause-library");
@include workspace-toolbar-search;
@include workspace-button-system;

.create-shell {
  background:
    radial-gradient(circle at right top, rgba(254, 194, 96, 0.16), transparent 30%),
    linear-gradient(135deg, rgba(140, 21, 21, 0.05) 0%, rgba(255, 255, 255, 0.96) 54%, rgba(255, 248, 237, 0.98) 100%);
  border: 1px solid #eadfce;
  border-radius: 22px;
  box-shadow: 0 16px 34px rgba(44, 52, 71, 0.08);
  margin-bottom: 16px;
}

.create-shell__body {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.create-shell__eyebrow {
  color: #8c1515;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.create-shell__title {
  color: #223045;
  font-size: 1.18rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.create-shell__copy {
  color: #66758a;
  line-height: 1.6;
  margin-top: 6px;
  max-width: 42rem;
}

.create-shell__button {
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(140, 21, 21, 0.16);
  min-height: 46px;
  min-width: 182px;
  padding: 10px 18px;
}

.create-shell__note {
  color: #66758a;
  font-size: 0.92rem;
  line-height: 1.6;
  max-width: 26rem;
  text-align: right;
}

.card-shell,
.modal-card {
  background: #fff;
  border: 1px solid #e9edf3;
  border-radius: 18px;
  box-shadow: 0 14px 30px rgba(44, 52, 71, 0.06);
}

.card-header,
.modal-header,
.modal-actions {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.card-header {
  padding: 18px 20px;
}

.result-title {
  color: #344767;
  font-size: 1rem;
  font-weight: 700;
}

.result-copy {
  color: #718096;
  font-size: 0.92rem;
  margin-top: 2px;
}

.result-badge {
  background: #fff4df;
  border-radius: 999px;
  color: #8c1515;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 8px 12px;
}

.clause-list {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  padding: 0 20px 20px;
}

.clause-card {
  background: linear-gradient(180deg, #fffefc 0%, #ffffff 100%);
  border: 1px solid #eef1f6;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 18px;
}

.clause-card__header {
  display: grid;
  gap: 10px;
}

.clause-card__eyebrow,
.clause-card__footer {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.clause-title {
  color: #2d3748;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.category-pill,
.source-pill {
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 10px;
}

.category-pill {
  background: #f7e8eb;
  color: #8a1728;
}

.source-pill {
  background: #fff7e6;
  color: #9a6700;
}

.clause-content {
  color: #52606d;
  flex: 1;
  line-height: 1.75;
  margin-top: 14px;
  white-space: pre-wrap;
  position: relative;
  cursor: pointer;
}
.clause-content--collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.clause-content__toggle {
  background: transparent;
  border: none;
  color: #8c1515;
  cursor: pointer;
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 600;
  margin-top: 8px;
  padding: 0;
  text-decoration: underline;
}
.clause-content__toggle:hover {
  opacity: 0.8;
}
.clause-card--expanded {
  border-color: #dfbf8a;
  background: #fffaf2;
}

.clause-card__footer,
.modal-actions {
  margin-top: 18px;
}

.modal-overlay { align-items: center; background: rgba(15, 23, 42, 0.5); display: flex; inset: 0; justify-content: center; padding: 24px; position: fixed; z-index: 1000; }
.modal-card { max-width: 640px; padding: 20px; width: 100%; }
.empty-state { align-items: center; color: #718096; display: flex; flex-direction: column; gap: 8px; padding: 36px 20px; text-align: center; }
.empty-state__icon { font-size: 2rem; }
.empty-state__title { color: #344767; font-size: 1.1rem; font-weight: 700; }
.close-btn { background: transparent; border: 0; cursor: pointer; }
.modal-body { display: grid; gap: 12px; margin-top: 12px; }
.field { display: grid; gap: 8px; }
.field span { color: #344767; font-weight: 700; }
.field input, .field textarea { border: 1px solid #dde4ec; border-radius: 12px; padding: 12px; }
.detail-row { align-items: center; border-top: 1px solid #eef1f6; display: flex; gap: 16px; justify-content: space-between; padding-top: 12px; }
.detail-row--stacked { align-items: flex-start; flex-direction: column; }
.detail-row span { color: #718096; font-weight: 600; }
.detail-copy { color: #52606d; line-height: 1.75; white-space: pre-wrap; }
@media (max-width: 768px) {
  .toolbar,
  .create-shell__body,
  .card-header,
  .modal-header,
  .modal-actions,
  .detail-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .filter-select {
    width: 100%;
  }

  .create-shell__button {
    min-width: 100%;
    width: 100%;
  }

  .create-shell__note {
    max-width: none;
    text-align: left;
  }
}
</style>
